import { useEffect, useMemo, useRef, useState } from "react";
import { validatePhoneNumberLength } from "libphonenumber-js";
import { z } from "zod";
import { MiCheckbox, MiDatePicker } from "@miplatform/components";
import {
  CONTACT_STATUSES,
  DATEPICKER_ACTIONS,
  DATEPICKER_VARIANTS,
  DATE_TIME_FORMATS,
  DOCUMENT_TYPES,
  ENTRY_TYPE_OPTIONS,
  HOME_TYPE_OPTIONS,
  INPUT_TYPES,
  PERMISSIONS,
  PREFERRED_CONTACT_METHOD_OPTIONS,
  PROPERTY_TYPE,
  PROPERTY_TYPE_OPTIONS,
  SWING_DIRECTION_OPTIONS,
  WATER_HEATER_TYPE_OPTIONS,
  emailRegex,
  phoneRegex,
  regionCodesUS,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { AccountType } from "@miplatform/types";
import {
  addToDate,
  externalReferenceId,
  extractDigits,
  getDate,
  getPhoneNumber,
  maxInputLength,
} from "@miplatform/utils";
import { useCityViewModel } from "@miplatform/view-models";
import { usePermissions } from "@mibuildweb/hooks";
import { Box } from "@mui/material";
import { CUSTOMER_OPTIONS } from "../../views/Accounts/accountsConstants";
import { HomeAndAccountSearch } from "../HomeAndAccountSearch/HomeAndAccountSearch";
import { OptionChip } from "../OptionChip/OptionChip";
import { CityAutocomplete } from "./CityAutocomplete/CityAutocomplete";
import { ADD_HOME_SECTIONS } from "./addHomeConstants";
import { useAddHomeFormViewModel } from "./useAddHomeFormViewModel";

const INITIAL_CONTACT = {
  accountId: "",
  accountType: AccountType.Service,
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  title: "",
  preferredContactMethod: "Phone",
  isVip: false,
};

const validationRules = [
  {
    name: "cityId",
    validationRule: z.string().min(1, "City is required"),
  },
  {
    name: "addressLine1",
    validationRule: z
      .string()
      .min(1, "Address line 1 is required")
      .regex(
        /^[^:"]*$/,
        "Address line 1 cannot contain colons or double quotes"
      ),
  },
  {
    name: "addressLine2",
    validationRule: z
      .string()
      .regex(
        /^[^:"]*$/,
        "Address line 2 cannot contain colons or double quotes"
      )
      .optional(),
  },
  {
    name: "state",
    validationRule: z.string().min(0, "State is required"),
  },
  {
    name: "zip",
    validationRule: z.string().regex(/^\d{5}$/, "Zip code must be 5 digits"),
  },
];

export const useAddHomeForm = ({
  hideCreateNewOption = false,
  readOnlyFields = [],
  account,
  builderId,
  communityId,
  city,
  regionCode,
  isCustomerTypeReadOnly = false,
  filterByAccountTypes = [AccountType.Builder, AccountType.Service],
  propertyType = PROPERTY_TYPE.NEW_HOME_CONSTRUCTION,
  onCreate,
  initialAddress,
  quickBooksId,
}) => {
  const {
    builders,
    communities,
    isLoading,
    contactCategories,
    documentCategory,
    allowedFileFormats,
    orderTemplateOptions,
    getLatLng,
    uploadDocument,
    createAccount,
    createHome,
    addContactRelationship,
    createContact,
    getContactCategories,
    getDocumentCategoryOptions,
    getBuilders,
    getCommunities,
    getOrderTemplates,
  } = useAddHomeFormViewModel();
  const contactIdRef = useRef(null);
  const coordinateRequestIdRef = useRef(0);
  const { hasPermission } = usePermissions();
  const { allCities, getCities } = useCityViewModel();
  const [homeContacts, setHomeContacts] = useState([]);
  const [isAddCityModalOpen, setIsAddCityModalOpen] = useState(false);
  const [showCreateContact, setShowCreateContact] = useState(false);
  const [isContactEditing, setIsContactEditing] = useState(false);
  const [files, setFiles] = useState([]);

  const edit = useEdit(
    {
      ...INITIAL_CONTACT,
      countryCode: "USA",
      propertyType,
      closingDate: null,
      communityId: communityId || null,
      builderId: builderId || null,
      cityId: "",
      addressLine1: initialAddress?.addressLine1 || "",
      addressLine2: initialAddress?.addressLine2 || "",
      zip: initialAddress?.zip || "",
      state: initialAddress?.state || regionCode || "",
    },
    validationRules
  );

  const handleUpdateCoordinates = async (overrides = {}) => {
    const requestId = ++coordinateRequestIdRef.current;

    const state = overrides.state ?? edit.getValue("state");
    const city = overrides.city ?? edit.getValue("city");
    const addressLine1 =
      overrides.addressLine1 ?? edit.getValue("addressLine1");
    const addressLine2 =
      overrides.addressLine2 ?? edit.getValue("addressLine2");
    const zip = overrides.zip ?? edit.getValue("zip");

    const coordinates = await getLatLng({
      state,
      city,
      address: `${addressLine1} ${addressLine2}`,
      zip,
    });

    if (requestId !== coordinateRequestIdRef.current) {
      return;
    }

    if (coordinates) {
      edit.update({
        latitude: coordinates.lat,
        longitude: coordinates.lng,
        isLocationPrecise: coordinates.isLocationPrecise,
      });
    }
  };

  const handleAddCityModalOpen = () => setIsAddCityModalOpen(true);

  const handleAddCityModalClose = () => setIsAddCityModalOpen(false);

  const handleAccountSelect = (value) => {
    const isBuilder = value?.accountType === AccountType.Builder;

    edit.update({
      customer: value,
      ...(isBuilder
        ? {
            builderId: value?.id,
            communityId: null,
            cityId: "",
            city: "",
            state: "",
            countyName: "",
            latitude: "",
            longitude: "",
          }
        : {
            builderId: null,
            communityId: null,
            cityId: "",
            city: "",
            state: "",
            countyName: "",
            latitude: "",
            longitude: "",
          }),
    });
  };

  useEffect(() => {
    const selectedBuilderId = edit.getValue("builderId");

    if (selectedBuilderId) {
      getCommunities(selectedBuilderId);
      return;
    }

    getCommunities();
  }, [edit.getValue("builderId")]);

  const handleAccountReset = () => {
    edit.update({ customer: undefined });
    edit.reset();
  };

  const handleFileChange = (files) => {
    setFiles(files);
  };

  const isCommunityRequired = useMemo(
    () =>
      edit.getValue("propertyType") === PROPERTY_TYPE.NEW_HOME_CONSTRUCTION &&
      !!edit.getValue("builderId"),
    [edit.getValue("propertyType"), edit.getValue("builderId")]
  );

  const handleCommunityChange = (event) => {
    const id = event.target.value;

    const selectedCommunity = communities.find(
      (community) => community.id === id
    );

    const selectedCity =
      allCities.find((city) => city.id === selectedCommunity?.city?.id) ??
      selectedCommunity?.city;

    const builderId = edit.getValue("builderId");

    edit.update({
      communityId: id,
      ...(selectedCommunity && selectedCity
        ? {
            cityId: selectedCity.id,
            city: selectedCity.cityName,
            state: selectedCity.regionCode,
            countyName: selectedCity.countyName,
          }
        : {
            cityId: "",
            city: "",
            state: "",
            countyName: "",
          }),
      ...(!builderId && selectedCommunity
        ? {
            builderId: selectedCommunity?.builder?.id,
            customer: {
              id: selectedCommunity?.builder?.id,
              value: selectedCommunity?.builder?.id,
              name: selectedCommunity?.builder?.name,
            },
          }
        : {}),
    });
  };

  const detailFields = {
    [ADD_HOME_SECTIONS.CUSTOMER_SEARCH]: [
      {
        label: "Search For Customer",
        name: "customer",
        fieldType: INPUT_TYPES.CUSTOM,
        customComponent: (
          <HomeAndAccountSearch
            type="account"
            hideCreateNewOption={
              hideCreateNewOption ||
              !hasPermission(PERMISSIONS.SERVICE_CUSTOMER.CREATE)
            }
            filterByAccountTypes={filterByAccountTypes}
            account={account}
            value={edit.getValue("customer")}
            disabled={showCreateContact || account}
            onSelect={handleAccountSelect}
            onReset={handleAccountReset}
            onCreate={() => {
              setShowCreateContact(true);
              edit.update(INITIAL_CONTACT);
            }}
          />
        ),
      },
      {
        label: "Community",
        name: "communityId",
        fieldType: INPUT_TYPES.SELECT,
        options: communities.map(({ name, id }) => ({
          label: name,
          value: id,
        })),
        disabled: isLoading || readOnlyFields.includes("communityId"),
        required: isCommunityRequired,
        width: "100%",
        handleChange: handleCommunityChange,
      },
    ],
    [ADD_HOME_SECTIONS.PROPERTY_TYPE]: [
      {
        label: "Property Type",
        name: "propertyType",
        fieldType: INPUT_TYPES.SELECT,
        options: PROPERTY_TYPE_OPTIONS,
        handleChange: ({ target: { value } }) => {
          edit.update({
            propertyType: value,
            ...(value === PROPERTY_TYPE.SERVICE && {
              isATakeover: false,
              takeOverStageId: null,
            }),
          });
        },
        required: true,
      },
    ],
    [ADD_HOME_SECTIONS.PROPERTY_INFORMATION_CHECKBOXES]: [
      {
        label: "V.I.P.",
        name: "isVIP",
        fieldType: INPUT_TYPES.CHECKBOX,
        backgroundColor: "inherit",
        minWidth: 70,
        width: 70,
      },
      {
        label: "Model Home",
        name: "isModelHome",
        fieldType: INPUT_TYPES.CHECKBOX,
        backgroundColor: "inherit",
      },
    ],
    [ADD_HOME_SECTIONS.ADDRESS_INFORMATION]: [
      {
        label: "Address (Line 1)",
        name: "addressLine1",
        width: "50%",
        required: true,
        onBlur: handleUpdateCoordinates,
      },
      {
        label: "Address (Line 2)",
        name: "addressLine2",
        width: "50%",
        onBlur: handleUpdateCoordinates,
      },
      {
        name: "cityId",
        label: "City",
        width: "33%",
        fieldType: INPUT_TYPES.CUSTOM,
        customComponent: (
          <CityAutocomplete
            value={edit.edits.cityId}
            cities={allCities}
            getCities={getCities}
            onChange={(city) => {
              edit.update({
                cityId: city.id,
                city: city.cityName,
                countyName: city.countyName,
                state: city.regionCode,
              });
              handleUpdateCoordinates({
                city: city.cityName,
                state: city.regionCode,
              });
            }}
            onCreate={getCities}
          />
        ),
      },
      {
        label: "State",
        name: "state",
        required: true,
        fieldType: INPUT_TYPES.SELECT,
        disabled: readOnlyFields.includes("regionCode"),
        options: regionCodesUS.map((code) => ({ label: code, value: code })),
        handleChange: (e) => {
          edit.update({ state: e.target.value });
          handleUpdateCoordinates({ state: e.target.value });
        },
      },
      {
        label: "Zip",
        name: "zip",
        required: true,
        beforeOnChange: (value) => maxInputLength(value, 5),
        onBlur: handleUpdateCoordinates,
      },
      {
        label: "County",
        name: "countyName",
        disabled: true,
        width: "50%",
      },
      {
        label: "Country",
        name: "countryCode",
        disabled: true,
        width: "50%",
      },
    ],
    [ADD_HOME_SECTIONS.ADDITIONAL_INFORMATION]: [
      {
        label: "Plot",
        name: "plot",
        width: "33.3%",
      },
      {
        label: "Lot",
        name: "lot",
        width: "33.3%",
      },
      {
        label: "Block",
        name: "block",
        width: "33.3%",
      },
      {
        label: "Latitude",
        name: "latitude",
        width: "50%",
        handleChange: (e) => {
          edit.update({
            latitude: e.target.value,
            isLocationPrecise: true,
          });
        },
      },
      {
        label: "Longitude",
        name: "longitude",
        width: "50%",
        handleChange: (e) => {
          edit.update({
            longitude: e.target.value,
            isLocationPrecise: true,
          });
        },
      },
      {
        label: "Builder",
        name: "builderId",
        fieldType: INPUT_TYPES.SELECT,
        options: builders,
        readOnly:
          edit.getValue("customer")?.accountType === AccountType.Builder ||
          readOnlyFields.includes("builderId"),
        width: "50%",
      },
      {
        label: "Property Closing Date",
        fieldType: INPUT_TYPES.CUSTOM,
        name: "closingDate",
        customComponent: (
          <MiDatePicker
            isFullWidth
            label="Closing Date"
            disableFuture={
              edit.getValue("propertyType") === PROPERTY_TYPE.SERVICE
            }
            onChange={(date) => {
              const warrantyPeriodInMonths = builders.find(
                (b) => b.id === edit.getValue("builderId")
              )?.warrantyPeriodInMonths;
              edit.update({
                closingDate: date
                  ? getDate(date).format(
                      DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT
                    )
                  : null,
                warrantyEndDate:
                  date && warrantyPeriodInMonths
                    ? addToDate(date, warrantyPeriodInMonths, "months").format(
                        DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT
                      )
                    : null,
              });
            }}
            variant={DATEPICKER_VARIANTS.DESKTOP}
            actions={[
              DATEPICKER_ACTIONS.TODAY,
              DATEPICKER_ACTIONS.CLEAR,
              DATEPICKER_ACTIONS.CANCEL,
            ]}
          />
        ),
        width: "50%",
      },
      {
        label: "Warranty End Date",
        name: "warrantyEndDate",
        fieldType: INPUT_TYPES.CUSTOM,
        customComponent: (
          <MiDatePicker
            label="Warranty End Date"
            disabled={!edit.getValue("closingDate")}
            isFullWidth
            onChange={(date) => {
              edit.update({
                warrantyEndDate: date
                  ? getDate(date).format(
                      DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT
                    )
                  : null,
              });
            }}
            variant={DATEPICKER_VARIANTS.DESKTOP}
            actions={[
              DATEPICKER_ACTIONS.RESET,
              DATEPICKER_ACTIONS.CANCEL,
              DATEPICKER_ACTIONS.CLEAR,
            ]}
          />
        ),
        width: "50%",
      },
    ],
    [ADD_HOME_SECTIONS.PROPERTY_INFORMATION]: [
      {
        label: "Home Type",
        name: "homeType",
        fieldType: INPUT_TYPES.SELECT,
        options: HOME_TYPE_OPTIONS,
        width: "33.3%",
      },
      {
        label: "Plan Name",
        name: "planName",
        fieldType: INPUT_TYPES.SELECT,
        options: [],
        width: "33.3%",
      },
      {
        label: "Plan Series",
        name: "planSeries",
        fieldType: INPUT_TYPES.SELECT,
        options: [],
        width: "33.3%",
      },
      {
        label: "Plan Spec",
        name: "planSpec",
        fieldType: INPUT_TYPES.SELECT,
        options: [],
        width: "33.3%",
      },
      {
        label: "Swing Direction",
        name: "swingDirection",
        fieldType: INPUT_TYPES.SELECT,
        options: SWING_DIRECTION_OPTIONS,
        width: "33.3%",
      },
      {
        label: "Entry (Direction of Door)",
        name: "entryType",
        fieldType: INPUT_TYPES.SELECT,
        options: ENTRY_TYPE_OPTIONS,
        width: "33.3%",
      },
      {
        label: "Water Heater Type",
        name: "complexity",
        fieldType: INPUT_TYPES.SELECT,
        options: WATER_HEATER_TYPE_OPTIONS,
        width: "25%",
      },
      {
        label: "Elevation",
        name: "elevation",
        fieldType: INPUT_TYPES.SELECT,
        options: [],
        width: "25%",
      },
      {
        label: "Takeover",
        name: "isATakeover",
        fieldType: INPUT_TYPES.CUSTOM,
        backgroundColor: "inherit",
        customComponent: (
          <Box mt={1}>
            <MiCheckbox
              isFullWidth
              label="Takeover"
              value={edit.getValue("isATakeover") || false}
              handleChange={(e) => {
                edit.update({
                  isATakeover: e.target.checked,
                  ...(!e.target.checked && {
                    takeOverStageId: null,
                  }),
                });
              }}
            />
          </Box>
        ),
        width: "1%",
        padding: 0,
        flexGrow: 0,
      },
      {
        label: "Takeover Stage",
        name: "takeOverStageId",
        fieldType: INPUT_TYPES.SELECT,
        renderOption: (option) => <OptionChip {...option} />,
        options: orderTemplateOptions,
        width: "30%",
        disabled: !edit.getValue("isATakeover"),
        required: edit.getValue("isATakeover"),
      },
    ],
    [ADD_HOME_SECTIONS.CREATE_CUSTOMER]: [
      {
        label: "Customer Type",
        name: "accountType",
        width: "100%",
        fieldType: INPUT_TYPES.SELECT,
        options: CUSTOMER_OPTIONS,
        readOnly: isCustomerTypeReadOnly,
        required: true,
      },
      { label: "First Name", name: "firstName", width: "50%", required: true },
      { label: "Last Name", name: "lastName", width: "50%", required: true },
      {
        label: "Title",
        name: "title",
        width: "50%",
      },
      {
        label: "Preferred Contact Method",
        name: "preferredContactMethod",
        fieldType: INPUT_TYPES.SELECT,
        options: PREFERRED_CONTACT_METHOD_OPTIONS,
        width: "50%",
        required: true,
      },
      {
        label: "Phone Number",
        name: "phone",
        handleChange: (e) => {
          if (
            validatePhoneNumberLength(getPhoneNumber(e.target.value), "US") ===
            "TOO_LONG"
          ) {
            return;
          }

          edit.update({
            phone: getPhoneNumber(e.target.value),
          });
        },
        required: true,
      },
      { label: "Email Address", name: "email", required: true },
      {
        label: "Relationship Type",
        name: "relationshipType",
        fieldType: "select",
        options: contactCategories,
        required: true,
      },
      {
        label: "Point of Contact",
        name: "pointOfContact",
        fieldType: INPUT_TYPES.CHECKBOX,
        flexGrow: 0,
        backgroundColor: "inherit",
        padding: 0,
        width: 180,
      },
      {
        label: "VIP",
        name: "isVip",
        fieldType: INPUT_TYPES.CHECKBOX,
        flexGrow: 0,
        backgroundColor: "inherit",
        padding: 0,
      },
    ],
  };

  useEffect(() => {
    getDocumentCategoryOptions();
    getBuilders();
    getCities();
    getContactCategories();
    getOrderTemplates();
  }, []);

  useEffect(() => {
    edit.update({
      ...(builderId && { builderId }),
      ...(communityId && { communityId }),
      ...(city && { city }),
      ...(regionCode && { state: regionCode }),
    });
  }, [builderId, communityId, city, regionCode]);

  const onResetForm = () => {
    edit.reset({
      ...INITIAL_CONTACT,
      countryCode: "USA",
      propertyType,
      closingDate: null,
      ...(builderId && { builderId }),
      ...(communityId && { communityId }),
      ...(city && { city }),
      ...(regionCode && { state: regionCode }),
    });
    setHomeContacts([]);
  };

  const onCancelContactCreation = () => {
    setShowCreateContact(false);
  };

  const createCustomer = async () => {
    const name = `${edit.getValue("firstName")} ${edit.getValue("lastName")}`;

    const account = await createAccount({
      name,
      email: edit.getValue("email"),
      phone: extractDigits(edit.getValue("phone")),
      title: edit.getValue("title"),
      preferredContactMethod: edit.getValue("preferredContactMethod"),
      isVip: edit.getValue("isVip"),
      accountType: edit.getValue("accountType"),
      address: {
        addressLine1: edit.getValue("addressLine1"),
        addressLine2: edit.getValue("addressLine2"),
        city: edit.getValue("city"),
        regionCode: edit.getValue("state"),
        postalCode: edit.getValue("zip"),
        countryCode: "USA",
      },
      status: CONTACT_STATUSES.ACTIVE,
    });

    if (account) {
      edit.update({ customer: account });
      onCancelContactCreation();
      contactIdRef.current = await createContact({
        accountId: account.id,
        data: {
          ...edit.getValue(),
          displayName: name,
          phone: extractDigits(edit.getValue("phone")),
          status: CONTACT_STATUSES.ACTIVE,
        },
      });
    }
  };

  const handleCreateHome = async () => {
    const accountId = edit.getValue("customer")?.id;
    const latitude = edit.getValue("latitude");
    const longitude = edit.getValue("longitude");

    const data = {
      ...edit.getValue(),
      accountId,
      // Undefined is passed to avoid sending empty string because BE doesn't handle it properly
      builderId: builderId || edit.getValue("builderId") || undefined,
      address: {
        addressLine1: edit.getValue("addressLine1"),
        addressLine2: edit.getValue("addressLine2"),
        city: edit.getValue("city"),
        regionCode: edit.getValue("state"),
        postalCode: edit.getValue("zip"),
        countryCode: edit.getValue("countryCode"),
        isLocationPrecise: edit.getValue("isLocationPrecise"),
        ...((latitude || longitude) && {
          location: {
            latitude,
            longitude,
          },
        }),
      },
      quickBooksId,
    };

    const home = await createHome({ data });

    if (home) {
      if (contactIdRef.current) {
        addContactRelationship({
          accountId,
          contactId: contactIdRef.current,
          homeId: home.id,
          label: edit.getValue("relationshipType"),
          isPrimary: true,
        });
      }

      onResetForm();

      if (files.length > 0) {
        const uploadPromises = files.map((file) =>
          uploadDocument({
            file,
            documentType: DOCUMENT_TYPES.UNKNOWN,
            category: documentCategory,
            externalReferenceId: externalReferenceId.getHomeRefId({
              homeId: home.id,
            }),
          })
        );

        await Promise.all(uploadPromises);
      }

      if (homeContacts.length > 0) {
        const contactPromises = homeContacts.map((contact) =>
          addContactRelationship({
            accountId: contact.accountId,
            contactId: contact.id,
            homeId: home.id,
            label: contact.label,
            isPrimary: contact.isPrimary,
          })
        );

        await Promise.all(contactPromises);
      }

      onCreate(home);
      return home;
    }
    return null;
  };

  const isCreateCustomerDisabled =
    !edit.allFilled(
      "firstName",
      "lastName",
      "preferredContactMethod",
      "relationshipType",
      "phone",
      "email"
    ) ||
    !phoneRegex.test(edit.getValue("phone")) ||
    !emailRegex.test(edit.getValue("email"));

  const isSubmitDisabled =
    isLoading ||
    isContactEditing ||
    !edit.isValid() ||
    !edit.allFilled("addressLine1", "cityId", "state", "zip", "customer") ||
    files.some((file) => file.error) ||
    (edit.getValue("isATakeover") && !edit.getValue("takeOverStageId")) ||
    (edit.getValue("propertyType") === PROPERTY_TYPE.NEW_HOME_CONSTRUCTION &&
      !edit.allFilled("communityId"));

  return {
    edit,
    homeContacts,
    detailFields,
    isLoading,
    showCreateContact,
    allowedFileFormats,
    isSubmitDisabled,
    isCreateCustomerDisabled,
    isAddCityModalOpen,
    setIsContactEditing,
    setHomeContacts,
    handleFileChange,
    handleAddCityModalClose,
    handleAddCityModalOpen,
    handleCreateHome,
    createCustomer,
    onResetForm,
    onCancelContactCreation,
  };
};
